// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { infoCardStyles } from "@conklin/App/Components/InfoCards/InfoCard";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { color, ellipsis, linkArrowStyle, xScreen } from "@conklin/XDL/Utilities";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

infoCardStyles.wrapper = {
    css: css`
        display: flex;
        width: 100%;
        height: 100%;
        @media print {
            page-break-inside: avoid;
        }
        ${({ imagePosition }: any) => {
            return css`
                flex-direction: ${imagePosition};
            `;
        }}
    `,
};
infoCardStyles.imageClickableWrapper = {
    css: css`
        flex: 0 0 auto;
        max-height: 100%;
        overflow: hidden;
        border-radius: 4px;
        > div {
            width: 100%;
        }
        ${({ imagePosition }: any) =>
            imagePosition === "column" || imagePosition === "column-reverse"
                ? css`
                      height: 161px;
                      width: 100%;
                      text-align: center;
                      ${xScreen("lte", "lg")} {
                          height: 136px;
                      }
                      ${xScreen("lte", "md")} {
                          height: 156px;
                      }
                      ${xScreen("lte", "sm")} {
                          height: 141px;
                      }
                  `
                : css`
                      width: 124px;
                      ${xScreen("lte", "md")} {
                          width: 106px;
                      }
                      ${xScreen("lte", "sm")} {
                          width: 0;
                          display: none;
                      }
                  `}
    `,
};
infoCardStyles.image = {
    css: css`
        width: auto;
        height: auto;
        max-height: 100%;
        max-width: 100%;
        object-fit: contain;
        &,
        img {
            aspect-ratio: 376 / 161;
            ${xScreen("lte", "lg")} {
                aspect-ratio: 317 / 136;
            }
            ${xScreen("lte", "md")} {
                aspect-ratio: 358 / 154;
            }
            ${xScreen("lte", "sm")} {
                aspect-ratio: 328 / 141;
            }
        }
    `,
};

infoCardStyles.cardContentWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        flex: 1;
        padding-top: 16px;
    `,
};
infoCardStyles.subtitle = {
    typographyProps: {
        size: 12,
        weight: "bold",
        css: css`
            margin-bottom: 4px;
            line-height: 15px;
            ${ellipsis(1)}
        `,
    },
    css: css`
        &:hover {
            & > ${TypographyStyle} {
                color: ${color("common.ui10")};
            }
        }
    `,
};
infoCardStyles.title = {
    forwardAs: "h2",
    css: css`
        margin-bottom: 8px;
        min-height: 38px;
        ${xScreen("lte", "sm")} {
            min-height: auto;
        }
        ${ellipsis(2)}
    `,
};
infoCardStyles.titleLink = {
    typographyProps: {
        size: 16,
        weight: fontConfig.weight.bold,
        lineHeight: "19px",
        css: css`
            margin-bottom: 8px;
            min-height: 38px;
            ${xScreen("lte", "sm")} {
                min-height: auto;
            }
            ${ellipsis(2)}
        `,
    },
    css: css`
        min-height: 38px;
        ${xScreen("lte", "sm")} {
            min-height: auto;
        }
        ${ellipsis(2)}
        &:hover {
            & > ${TypographyStyle} {
                color: ${color("common.ui10")};
            }
        }
    `,
};
infoCardStyles.textContent = {
    variant: "p",
    size: 14,
    css: css`
        margin-bottom: 16px;
        line-height: 22px;
        ${ellipsis(4)}
    `,
};
infoCardStyles.smallLink = linkArrowStyle();
infoCardStyles.link = linkArrowStyle("large");
infoCardStyles.buttonWrapper = {
    css: css`
        margin-top: auto;
        width: 100%;
        ${({ buttonAlignment }: any) =>
            buttonAlignment &&
            css`
                text-align: ${buttonAlignment};
            `}
    `,
};
